// action/saleActions.js
import { inventoryAPI, salesAPI, productAPI, customerAPI } from './api';

export const saleActions = {
    // Get customers with proper error handling
    getCustomers: async () => {
        try {
            const response = await customerAPI.getAll();
            if (response.status && response.data) {
                return { 
                    success: true, 
                    data: response.data
                };
            }
            return { success: false, message: 'No customers found', data: [] };
        } catch (error) {
            console.error('Error fetching customers:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch customers',
                data: []
            };
        }
    },

    // Create sale with complete business logic
    createSale: async (saleData) => {
        try {
            // Validate required fields
            if (!saleData.customer_id) {
                return { success: false, message: 'Customer is required' };
            }
            
            let items = saleData.items;
            if (!items || items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }
            
            // Validate each item
            for (const item of items) {
                if (!item.product_name || item.product_name.trim() === '') {
                    return { success: false, message: 'Product name is required for all items' };
                }
                
                if (!item.quantity || parseFloat(item.quantity) <= 0) {
                    return { success: false, message: 'Valid quantity is required' };
                }
                
                if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
                    return { success: false, message: 'Valid unit price is required' };
                }
            }
            
            // Calculate totals to ensure they're correct
            const subtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
            const discount_amount = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
            const tax_amount = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
            
            const exd_tax_total = subtotal - discount_amount;
            const spl_disc_amount = (exd_tax_total * (saleData.spl_disc_percent || 0)) / 100;
            const after_spl_disc = exd_tax_total - spl_disc_amount;
            const calculated_grand_total = after_spl_disc + tax_amount + 
                                          (saleData.adjustment || 0) + 
                                          (saleData.additional_charge || 0) + 
                                          (saleData.courier_charge || 0);
            
            // Clean up items - remove temporary ids
            const cleanedItems = items.map(item => {
                const {  ...rest } = item;
                
                return {
                    product_id: rest.product_id,
                    product_name: rest.product_name,
                    sku: rest.sku || '',
                    quantity: parseFloat(rest.quantity) || 0,
                    unit_price: parseFloat(rest.unit_price) || 0,
                    discount: parseFloat(rest.discount) || 0,
                    discount_amount: parseFloat(rest.discount_amount) || 0,
                    tax_percent: parseFloat(rest.tax_percent) || 0,
                    tax_amount: parseFloat(rest.tax_amount) || 0,
                    unit_cost: parseFloat(rest.unit_cost) || 0,
                    total_cost: parseFloat(rest.total_cost) || 0,
                    category_id: rest.category_id || null,
                    brand_id: rest.brand_id || null
                };
            });
            
            // Prepare payment history
            let paymentHistory = [];
            if (saleData.paid_amount > 0 && saleData.payment_mode) {
                paymentHistory = [{
                    date: saleData.sale_date,
                    amount: saleData.paid_amount,
                    method: saleData.payment_mode,
                    reference: null,
                    notes: 'Initial payment',
                    status: 'completed'
                }];
            }
            
            // Prepare data for API
            const saleDataToSend = {
                ...saleData,
                items: cleanedItems,
                payment_history: paymentHistory, // Include payment history
                subtotal: saleData.subtotal || subtotal,
                tax: saleData.tax || tax_amount,
                discount: saleData.discount_amount || discount_amount,
                grand_total: saleData.grand_total || calculated_grand_total,
                invoice_no: saleData.invoice_no || saleData.sales_reference || `INV-${Date.now()}`,
                sale_date: saleData.sale_date || new Date().toISOString().split('T')[0]
            };
            
            console.log('Sending to API with payment history:', JSON.stringify(saleDataToSend, null, 2));
            
            const response = await salesAPI.create(saleDataToSend);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data,
                    message: 'Sale created successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to create sale' 
                };
            }
        } catch (error) {
            console.error('Error creating sale:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create sale' 
            };
        }
    },

    // Get sales list with filters and pagination
   // In saleActions.js
getSales: async (params = {}) => {
    try {
        const response = await salesAPI.list(params);
        
        console.log('Raw API response in getSales:', response);
        
        // Handle the response structure from your updated PHP
        if (response.data && response.data.sales) {
            return {
                success: true,
                data: response.data.sales,
                total: response.data.total,
                page: response.data.page,
                limit: response.data.limit,
                totalPages: response.data.total_pages,
                summary: response.data.summary
            };
        } else if (response.sales) {
            return {
                success: true,
                data: response.sales,
                total: response.total,
                page: response.page,
                limit: response.limit,
                totalPages: response.total_pages
            };
        } else if (Array.isArray(response)) {
            return {
                success: true,
                data: response,
                total: response.length,
                page: 1,
                limit: response.length,
                totalPages: 1
            };
        } else {
            return {
                success: false,
                message: 'Invalid API response format',
                data: []
            };
        }
    } catch (error) {
        console.error('Error fetching sales:', error);
        return {
            success: false,
            message: error.message || 'Failed to fetch sales',
            data: []
        };
    }
},

    // Get single sale
    getSale: async (id) => {
        try {
            const response = await salesAPI.get(id);
            
            if (!response) {
                return { success: false, message: 'Sale not found' };
            }
            
            if (response.success && response.data) {
                return { 
                    success: true, 
                    data: response.data
                };
            } else if (response.data) {
                return { 
                    success: true, 
                    data: response.data
                };
            } else {
                return { 
                    success: true, 
                    data: response
                };
            }
        } catch (error) {
            console.error('Error fetching sale:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch sale' 
            };
        }
    },

    // Update sale
    updateSale: async (id, saleData) => {
        try {
            if (!id) {
                return { success: false, message: 'Sale ID is required' };
            }
            
            saleData.id = id;
            
            // Get existing sale to manage stock properly
            const existingSaleResponse = await salesAPI.get(id);
            if (!existingSaleResponse || !existingSaleResponse.data) {
                return { success: false, message: 'Sale not found' };
            }
            
            const existingSale = existingSaleResponse.data;
            
            // Parse items if they're in string format
            let newItems = saleData.items;
            if (typeof newItems === 'string') {
                try {
                    newItems = JSON.parse(newItems);
                } catch (e) {
                    return { success: false, message: 'Invalid items format' };
                }
            }
            
            if (!newItems || newItems.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }
            
            // Get existing items from the sale
            let existingItems = [];
            if (existingSale.items && Array.isArray(existingSale.items)) {
                existingItems = existingSale.items;
            } else {
                try {
                    const itemsResponse = await salesAPI.getItems(id);
                    if (itemsResponse && Array.isArray(itemsResponse)) {
                        existingItems = itemsResponse;
                    } else if (itemsResponse && itemsResponse.data) {
                        existingItems = itemsResponse.data;
                    }
                } catch (itemsError) {
                    console.error('Error fetching existing items:', itemsError);
                }
            }
            
            // Create maps for stock adjustments
            const existingItemsMap = new Map();
            existingItems.forEach(item => {
                if (item.product_id) {
                    existingItemsMap.set(item.product_id, {
                        quantity: parseFloat(item.quantity) || 0,
                        sale_item_id: item.id
                    });
                }
            });
            
            const newItemsMap = new Map();
            newItems.forEach(item => {
                if (item.product_id) {
                    newItemsMap.set(item.product_id, {
                        quantity: parseFloat(item.quantity) || 0,
                        item: item
                    });
                }
            });
            
            // Calculate stock adjustments
            const stockAdjustments = [];
            
            // Products removed or quantity decreased
            existingItemsMap.forEach((existingItem, productId) => {
                const newItem = newItemsMap.get(productId);
                const existingQty = existingItem.quantity;
                const newQty = newItem ? newItem.quantity : 0;
                
                if (newQty < existingQty) {
                    // Quantity decreased - add back to stock
                    const adjustment = existingQty - newQty;
                    stockAdjustments.push({
                        product_id: productId,
                        adjustment: adjustment, // Positive for adding back to stock
                        type: 'sale_update_decrease',
                        reference: `SALE-UPDATE-${id}`,
                        note: `Sale update: Quantity decreased from ${existingQty} to ${newQty}`
                    });
                }
            });
            
            // Products added or quantity increased
            newItemsMap.forEach((newItem, productId) => {
                const existingItem = existingItemsMap.get(productId);
                const newQty = newItem.quantity;
                const existingQty = existingItem ? existingItem.quantity : 0;
                
                if (newQty > existingQty) {
                    // Quantity increased - remove from stock
                    const adjustment = newQty - existingQty;
                    stockAdjustments.push({
                        product_id: productId,
                        adjustment: -adjustment, // Negative for removing from stock
                        type: 'sale_update_increase',
                        reference: `SALE-UPDATE-${id}`,
                        note: `Sale update: Quantity increased from ${existingQty} to ${newQty}`
                    });
                }
            });
            
            // Prepare sale data for update
            const saleDataToSend = {
                ...saleData,
                items: newItems,
                stock_adjustments: stockAdjustments
            };
            
            const response = await salesAPI.update(id, saleDataToSend);
            
            if (response && (response.success || response.status || response.id)) {
                // Apply stock adjustments after successful update
                if (stockAdjustments.length > 0) {
                    for (const adjustment of stockAdjustments) {
                        try {
                            await inventoryAPI.adjustStock({
                                product_id: adjustment.product_id,
                                quantity: adjustment.adjustment,
                                type: adjustment.type,
                                reference_no: adjustment.reference,
                                notes: adjustment.note
                            });
                        } catch (stockError) {
                            console.error('Error adjusting stock after update:', stockError);
                        }
                    }
                }
                
                return { 
                    success: true, 
                    data: {
                        ...response,
                        id: id
                    },
                    message: 'Sale updated successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to update sale' 
                };
            }
        } catch (error) {
            console.error('Error updating sale:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update sale' 
            };
        }
    },

    // Delete sale
    deleteSale: async (deleteData) => {
        try {
            if (!deleteData.id && !deleteData) {
                return { success: false, message: 'Sale ID is required' };
            }
            
            const id = typeof deleteData === 'object' ? deleteData.id : deleteData;
            const data = { id };
            
            const response = await salesAPI.delete(data);
            
            if (response && (response.success || response.status || response.deleted)) {
                return { 
                    success: true, 
                    data: response,
                    message: response.message || 'Sale deleted successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to delete sale' 
                };
            }
        } catch (error) {
            console.error('Error deleting sale:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete sale' 
            };
        }
    },

    // Create sale item
    createItem: async (itemData) => {
        try {
            const response = await salesAPI.createItem(itemData);
            return { 
                success: true, 
                data: response,
                message: 'Item added successfully'
            };
        } catch (error) {
            console.error('Error creating sale item:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create item' 
            };
        }
    },

    // Delete sale item
    deleteItem: async (id, saleId) => {
        try {
            // Get item first to reverse stock
            const sale = await saleActions.getSale(saleId);
            if (sale.success && sale.data.items) {
                const item = sale.data.items.find(i => i.id == id);
                if (item && item.product_id) {
                    // Add back to stock
                    await inventoryAPI.adjustStock({
                        product_id: item.product_id,
                        quantity: item.quantity,
                        type: 'item_removal',
                        reference_no: `ITEM-REM-${id}`,
                        notes: `Item removed from sale ${saleId}`
                    });
                }
            }
            
            const response = await salesAPI.deleteItem(id);
            return { 
                success: true, 
                data: response,
                message: 'Item deleted successfully'
            };
        } catch (error) {
            console.error('Error deleting sale item:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete item' 
            };
        }
    },

    // Update status
    updateStatus: async (id, status) => {
        try {
            const response = await salesAPI.updateStatus(id, status);
            return { 
                success: true, 
                data: response,
                message: `Status updated to ${status}`
            };
        } catch (error) {
            console.error('Error updating status:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update status' 
            };
        }
    },

    // Update payment status
    updatePaymentStatus: async (id, paymentStatus, paidAmount = 0) => {
        try {
            const updateData = {
                payment_status: paymentStatus,
                paid_amount: paidAmount
            };
            
            if (paymentStatus === 'paid') {
                const sale = await saleActions.getSale(id);
                if (sale.success) {
                    updateData.due_amount = 0;
                    updateData.paid_amount = sale.data.grand_total || 0;
                }
            }
            
            const response = await salesAPI.updatePaymentStatus(id, paymentStatus, updateData.paid_amount);
            return { 
                success: true, 
                data: response,
                message: 'Payment status updated'
            };
        } catch (error) {
            console.error('Error updating payment status:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update payment status' 
            };
        }
    },

    // Get payment history
    getPaymentHistory: async (saleId, page = 1, limit = 10) => {
        try {
            const response = await salesAPI.getPaymentHistory(saleId, page, limit);
            
            if (response.success) {
                return {
                    success: true,
                    data: response.data
                };
            }
            return {
                success: false,
                message: response.message || 'Failed to fetch payment history',
                data: { payments: [], summary: null, sale: null, pagination: { total: 0 } }
            };
        } catch (error) {
            console.error('Error fetching payment history:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch payment history',
                data: { payments: [], summary: null, sale: null, pagination: { total: 0 } }
            };
        }
    },

    // Add payment
    addPayment: async (paymentData) => {
        try {
            const response = await salesAPI.addPayment(paymentData);
            
            if (response.success) {
                return {
                    success: true,
                    data: response.data,
                    message: 'Payment added successfully'
                };
            }
            return {
                success: false,
                message: response.message || 'Failed to add payment'
            };
        } catch (error) {
            console.error('Error adding payment:', error);
            return {
                success: false,
                message: error.message || 'Failed to add payment'
            };
        }
    },

    // Delete payment
    deletePayment: async (paymentId) => {
        try {
            const response = await salesAPI.deletePayment(paymentId);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data,
                    message: 'Payment deleted successfully'
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to delete payment'
            };
        } catch (error) {
            console.error('Error deleting payment:', error);
            return {
                success: false,
                message: error.message || 'Failed to delete payment'
            };
        }
    },

    // Get invoice for printing
    getInvoice: async (id) => {
        try {
            const response = await salesAPI.getInvoice(id);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('Error getting invoice:', error);
            return {
                success: false,
                message: error.message || 'Failed to get invoice'
            };
        }
    }
};

// Product Actions for Sales
export const saleProductActions = {
    // Search products
    searchProducts: async (searchTerm) => {
        try {
            if (!searchTerm || searchTerm.length < 2) {
                return { success: true, data: [] };
            }
            
            const response = await productAPI.search(searchTerm);
            
            let products = [];
            if (Array.isArray(response)) {
                products = response;
            } else if (response && Array.isArray(response.data)) {
                products = response.data;
            } else if (response && response.products) {
                products = response.products;
            }
            
            const formattedProducts = products.map(product => ({
                value: product.id,
                label: `${product.product_name} (${product.sku || 'N/A'}) - ₹${product.selling_price || product.price || 0}`,
                product_name: product.product_name,
                sku: product.sku,
                selling_price: parseFloat(product.selling_price) || parseFloat(product.price) || 0,
                cost_price: parseFloat(product.cost_price) || 0,
                purchase_price: parseFloat(product.purchase_price) || 0,
                tax_rate: parseFloat(product.tax_rate) || 0,
                category_id: product.category_id,
                brand_id: product.brand_id,
                unit_id: product.unit_id,
                quantity: parseFloat(product.quantity) || 0,
                mrp: parseFloat(product.mrp) || 0,
                status: product.status
            }));
            
            return { 
                success: true, 
                data: formattedProducts,
                message: 'Products found'
            };
        } catch (error) {
            console.error('Error searching products:', error);
            return { success: true, data: [] };
        }
    },

    // Quick create product
    addProductQuick: async (productData) => {
        try {
            if (!productData.product_name || productData.product_name.trim() === '') {
                return { success: false, message: 'Product name is required' };
            }
            
            if (!productData.sku || productData.sku.trim() === '') {
                const timestamp = Date.now().toString(36);
                const random = Math.random().toString(36).substring(2, 6).toUpperCase();
                productData.sku = `SALE-${timestamp}-${random}`;
            }
            
            const defaultProduct = {
                product_slug: productData.product_name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now(),
                item_code: `ITM${Date.now().toString(36).toUpperCase()}`,
                product_type: 'Single',
                quantity: 0,
                price: parseFloat(productData.selling_price) || 0,
                cost_price: parseFloat(productData.cost_price) || 0,
                selling_price: parseFloat(productData.selling_price) || 0,
                mrp: (parseFloat(productData.selling_price) || 0) * 1.1,
                tax_type_id: null,
                tax_amount: 0,
                status: 'active',
                created_by: productData.created_by || 'Admin',
                selling_type: 'Transactional',
                rate_type: 'retail'
            };
            
            const completeProductData = {
                ...defaultProduct,
                ...productData
            };
            
            const response = await productAPI.quickCreate(completeProductData);
            
            if (response && (response.success || response.status || response.id)) {
                return { 
                    success: true, 
                    data: {
                        id: response.id || response.data?.id,
                        product_name: completeProductData.product_name,
                        sku: completeProductData.sku,
                        selling_price: completeProductData.selling_price,
                        cost_price: completeProductData.cost_price,
                        tax_rate: completeProductData.tax_rate || 0,
                        category_id: completeProductData.category_id,
                        brand_id: completeProductData.brand_id,
                        unit_id: completeProductData.unit_id
                    },
                    message: 'Product created successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to create product' 
                };
            }
        } catch (error) {
            console.error('Error creating quick product:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create product' 
            };
        }
    },

    // Get product by ID
    getProduct: async (id) => {
        try {
            const response = await productAPI.get(id);
            
            if (response && (response.id || response.data)) {
                const product = response.data || response;
                return {
                    success: true,
                    data: {
                        id: product.id,
                        product_name: product.product_name,
                        sku: product.sku,
                        selling_price: parseFloat(product.selling_price) || parseFloat(product.price) || 0,
                        cost_price: parseFloat(product.cost_price) || 0,
                        purchase_price: parseFloat(product.purchase_price) || 0,
                        tax_rate: parseFloat(product.tax_rate) || 0,
                        category_id: product.category_id,
                        brand_id: product.brand_id,
                        unit_id: product.unit_id,
                        quantity: parseFloat(product.quantity) || 0,
                        status: product.status
                    }
                };
            }
            
            return { success: false, message: 'Product not found' };
        } catch (error) {
            console.error('Error fetching product:', error);
            return { success: false, message: 'Failed to fetch product' };
        }
    },

    // Get master data for sales
    getSaleMasterData: async () => {
        try {
            const [customersRes, productsRes] = await Promise.allSettled([
                customerAPI.getAll(),
                productAPI.getAll({ limit: 100 })
            ]);
            
            const customers = customersRes.status === 'fulfilled' ? 
                (customersRes.value.data?.customers || customersRes.value) : [];
            
            const products = productsRes.status === 'fulfilled' ? 
                (productsRes.value.data?.products || productsRes.value) : [];
            
            return {
                success: true,
                data: {
                    customers: customers,
                    products: products
                },
                message: 'Master data loaded'
            };
        } catch (error) {
            console.error('Error fetching master data:', error);
            return {
                success: true,
                data: {
                    customers: [],
                    products: []
                }
            };
        }
    }
};

export default {
    saleActions,
    saleProductActions
};